import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  API, authHeaders, fmt, PageHeading, AdminCard,
  Modal, ModalHeader, TH, TD, inputStyle,
} from "./AdminUtils";

const EMPTY_FORM = {
  code: "",
  discountType: "PERCENTAGE",
  discountValue: "",
  minOrderAmount: "",
  maxDiscount: "",
  usageLimit: "",
  perUserLimit: "1",
  expiryDate: "",
};

function CouponTypeBadge({ type }) {
  const isPct = type === "PERCENTAGE";
  return (
    <span style={{
      fontSize: 9, fontFamily: "var(--font-mono)", letterSpacing: "0.1em",
      textTransform: "uppercase", padding: "2px 8px",
      borderRadius: "var(--radius-pill)",
      background: isPct ? "var(--accent-bg)" : "#0a1a2e",
      color: isPct ? "var(--accent)" : "#60a5fa",
      border: `1px solid ${isPct ? "var(--accent)" : "#1a3a5e"}`,
      display: "inline-block",
    }}>
      {isPct ? "%" : "Flat"}
    </span>
  );
}

function ActiveBadge({ active }) {
  return (
    <span style={{
      fontSize: 9, fontFamily: "var(--font-mono)", letterSpacing: "0.08em",
      textTransform: "uppercase", padding: "2px 8px",
      borderRadius: "var(--radius-pill)",
      background: active ? "var(--success-bg)" : "var(--danger-bg)",
      color: active ? "var(--success-text)" : "var(--danger-text)",
      border: `1px solid ${active ? "var(--success-border)" : "var(--danger-border)"}`,
      display: "inline-block",
    }}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [actionLoading, setActionLoading] = useState(null); // coupon id being acted on

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/coupons`, { headers: authHeaders() });
      setCoupons(res.data);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setFormOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      code: c.code || "",
      discountType: c.discountType || "PERCENTAGE",
      discountValue: c.discountValue ?? "",
      minOrderAmount: c.minOrderAmount ?? "",
      maxDiscount: c.maxDiscount ?? "",
      usageLimit: c.usageLimit ?? "",
      perUserLimit: c.perUserLimit ?? "1",
      expiryDate: c.expiryDate ? c.expiryDate.slice(0, 16) : "",
    });
    setFormError("");
    setFormOpen(true);
  };

  const closeForm = () => { setFormOpen(false); setEditingId(null); setFormError(""); };

  const handleSave = async () => {
    if (!form.code.trim()) { setFormError("Coupon code is required."); return; }
    if (!form.discountValue || isNaN(Number(form.discountValue)) || Number(form.discountValue) <= 0) {
      setFormError("Enter a valid discount value."); return;
    }
    if (!form.expiryDate) { setFormError("Expiry date is required."); return; }

    setSaving(true);
    setFormError("");
    const body = {
      code: form.code.trim().toUpperCase(),
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      minOrderAmount: form.minOrderAmount !== "" ? Number(form.minOrderAmount) : null,
      maxDiscount: form.maxDiscount !== "" ? Number(form.maxDiscount) : null,
      usageLimit: form.usageLimit !== "" ? Number(form.usageLimit) : null,
      perUserLimit: form.perUserLimit !== "" ? Number(form.perUserLimit) : 1,
      expiryDate: form.expiryDate + ":00",
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/coupons/${editingId}`, body, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/coupons`, body, { headers: authHeaders() });
      }
      closeForm();
      load();
    } catch (err) {
      const msg = err.response?.data;
      setFormError(typeof msg === "string" && msg ? msg : "Failed to save coupon.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    setActionLoading(id + "-toggle");
    try {
      await axios.patch(`${API}/admin/coupons/${id}/toggle`, null, { headers: authHeaders() });
      load();
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete coupon "${code}"? This cannot be undone.`)) return;
    setActionLoading(id + "-delete");
    try {
      await axios.delete(`${API}/admin/coupons/${id}`, { headers: authHeaders() });
      load();
    } finally {
      setActionLoading(null);
    }
  };

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div style={{ animation: "auModalIn 0.3s ease both" }}>
      <PageHeading
        title="Coupons"
        sub="Manage discount codes and promo offers"
        action={
          <button
            onClick={openCreate}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "9px 18px", borderRadius: "var(--radius-md)",
              background: "var(--accent)", border: "none",
              color: "var(--bg-primary)", fontSize: 12,
              letterSpacing: "0.06em", cursor: "pointer",
              fontFamily: "var(--font-mono)",
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New Coupon
          </button>
        }
      />

      <AdminCard>
        {loading ? (
          <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            Loading…
          </div>
        ) : coupons.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 12 }}>No coupons yet.</p>
            <button onClick={openCreate} style={{
              padding: "8px 16px", borderRadius: "var(--radius-md)",
              background: "var(--accent-bg)", border: "1px solid var(--accent)",
              color: "var(--accent)", fontSize: 12, cursor: "pointer",
              fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
            }}>
              Create your first coupon
            </button>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <TH>Code</TH>
                <TH>Type</TH>
                <TH>Value</TH>
                <TH>Min Order</TH>
                <TH>Max Disc.</TH>
                <TH>Usage</TH>
                <TH>Expires</TH>
                <TH>Status</TH>
                <TH>Actions</TH>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => {
                const isToggling = actionLoading === c.id + "-toggle";
                const isDeleting = actionLoading === c.id + "-delete";
                return (
                  <tr key={c.id} style={{ transition: "background 0.15s" }}>
                    <TD>
                      <span style={{
                        fontFamily: "var(--font-mono)", fontSize: 12,
                        letterSpacing: "0.1em", color: "var(--accent)",
                      }}>
                        {c.code}
                      </span>
                    </TD>
                    <TD><CouponTypeBadge type={c.discountType} /></TD>
                    <TD style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                      {c.discountType === "PERCENTAGE" ? `${c.discountValue}%` : fmt(c.discountValue)}
                    </TD>
                    <TD style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                      {c.minOrderAmount ? fmt(c.minOrderAmount) : "—"}
                    </TD>
                    <TD style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                      {c.maxDiscount ? fmt(c.maxDiscount) : "—"}
                    </TD>
                    <TD style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                      {(c.usageCount ?? 0)}{c.usageLimit ? ` / ${c.usageLimit}` : " / ∞"}
                    </TD>
                    <TD style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {c.expiryDate
                        ? new Date(c.expiryDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                        : "—"}
                    </TD>
                    <TD><ActiveBadge active={c.active} /></TD>
                    <TD>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {/* Edit */}
                        <button
                          onClick={() => openEdit(c)}
                          title="Edit"
                          style={{
                            padding: "5px 10px", borderRadius: "var(--radius-sm)",
                            background: "var(--bg-secondary)", border: "1px solid var(--border-subtle)",
                            color: "var(--text-muted)", fontSize: 11, cursor: "pointer",
                            fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                          }}
                        >
                          Edit
                        </button>
                        {/* Toggle */}
                        <button
                          onClick={() => handleToggle(c.id)}
                          disabled={isToggling}
                          title={c.active ? "Deactivate" : "Activate"}
                          style={{
                            padding: "5px 10px", borderRadius: "var(--radius-sm)",
                            background: c.active ? "var(--warning-bg)" : "var(--success-bg)",
                            border: `1px solid ${c.active ? "var(--warning-border)" : "var(--success-border)"}`,
                            color: c.active ? "var(--warning-text)" : "var(--success-text)",
                            fontSize: 11, cursor: isToggling ? "not-allowed" : "pointer",
                            opacity: isToggling ? 0.6 : 1,
                            fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                          }}
                        >
                          {isToggling ? "…" : c.active ? "Disable" : "Enable"}
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(c.id, c.code)}
                          disabled={isDeleting}
                          title="Delete"
                          style={{
                            padding: "5px 10px", borderRadius: "var(--radius-sm)",
                            background: "var(--danger-bg)", border: "1px solid var(--danger-border)",
                            color: "var(--danger-text)", fontSize: 11,
                            cursor: isDeleting ? "not-allowed" : "pointer",
                            opacity: isDeleting ? 0.6 : 1,
                            fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                          }}
                        >
                          {isDeleting ? "…" : "Delete"}
                        </button>
                      </div>
                    </TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </AdminCard>

      {/* ── Create / Edit Modal ── */}
      {formOpen && (
        <Modal onClose={closeForm} width={520}>
          <ModalHeader title={editingId ? "Edit Coupon" : "New Coupon"} onClose={closeForm} />

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Code */}
            <div>
              <label style={labelStyle}>Coupon Code *</label>
              <input
                style={{ ...inputStyle, textTransform: "uppercase", fontFamily: "var(--font-mono)", letterSpacing: "0.1em" }}
                placeholder="e.g. SAVE10"
                value={form.code}
                onChange={e => setF("code", e.target.value.toUpperCase())}
              />
            </div>

            {/* Type + Value */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Discount Type *</label>
                <select
                  style={{ ...inputStyle, cursor: "pointer" }}
                  value={form.discountType}
                  onChange={e => setF("discountType", e.target.value)}
                >
                  <option value="PERCENTAGE">Percentage (%)</option>
                  <option value="FLAT">Flat (₹)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>
                  Discount Value * {form.discountType === "PERCENTAGE" ? "(%)" : "(₹)"}
                </label>
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  placeholder={form.discountType === "PERCENTAGE" ? "e.g. 10" : "e.g. 200"}
                  value={form.discountValue}
                  onChange={e => setF("discountValue", e.target.value)}
                />
              </div>
            </div>

            {/* Min Order + Max Discount */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Min Order Amount (₹)</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  placeholder="e.g. 500"
                  value={form.minOrderAmount}
                  onChange={e => setF("minOrderAmount", e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Max Discount Cap (₹)</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  placeholder={form.discountType === "PERCENTAGE" ? "e.g. 200" : "—"}
                  value={form.maxDiscount}
                  disabled={form.discountType === "FLAT"}
                  onChange={e => setF("maxDiscount", e.target.value)}
                />
              </div>
            </div>

            {/* Usage Limit + Per User */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Total Usage Limit</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="1"
                  placeholder="Leave blank for unlimited"
                  value={form.usageLimit}
                  onChange={e => setF("usageLimit", e.target.value)}
                />
              </div>
              <div>
                <label style={labelStyle}>Per-User Limit</label>
                <input
                  style={inputStyle}
                  type="number"
                  min="1"
                  placeholder="Default: 1"
                  value={form.perUserLimit}
                  onChange={e => setF("perUserLimit", e.target.value)}
                />
              </div>
            </div>

            {/* Expiry */}
            <div>
              <label style={labelStyle}>Expiry Date & Time *</label>
              <input
                style={inputStyle}
                type="datetime-local"
                value={form.expiryDate}
                onChange={e => setF("expiryDate", e.target.value)}
              />
            </div>

            {formError && (
              <p style={{
                fontSize: 12, color: "var(--danger-text)", padding: "8px 12px",
                background: "var(--danger-bg)", border: "1px solid var(--danger-border)",
                borderRadius: "var(--radius-sm)",
              }}>
                {formError}
              </p>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 4 }}>
              <button
                onClick={closeForm}
                style={{
                  padding: "9px 20px", borderRadius: "var(--radius-md)",
                  background: "transparent", border: "1px solid var(--border-subtle)",
                  color: "var(--text-muted)", fontSize: 12, cursor: "pointer",
                  fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: "9px 24px", borderRadius: "var(--radius-md)",
                  background: "var(--accent)", border: "none",
                  color: "var(--bg-primary)", fontSize: 12,
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.7 : 1,
                  fontFamily: "var(--font-mono)", letterSpacing: "0.06em",
                }}
              >
                {saving ? "Saving…" : editingId ? "Update Coupon" : "Create Coupon"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: 10,
  color: "var(--text-muted)",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  fontFamily: "var(--font-mono)",
  marginBottom: 6,
};
